/**
 * TradePanel — composite container for the token trading sidebar.
 *
 * Orchestrates: SwapForm (5.1), BalanceDisplay (5.2), PermitApproval (5.2),
 * useSwap (5.3), TransactionStateIndicator, and DefiDisclaimer (5.4).
 *
 * ARCH-16: Each sub-component owns its own data fetching.
 * Story 5.4 Task 1
 */

import { useCallback, useMemo, useState } from "react";
import { parseUnits } from "viem";

import { usePermit2Allowance } from "../../hooks/chain/use-permit2-allowance";
import { useSwap } from "../../hooks/chain/use-swap";
import { useSwapQuote } from "../../hooks/chain/use-swap-quote";
import { useTokenBalance } from "../../hooks/chain/use-token-balance";
import { useWallet } from "../../hooks/use-wallet";
import { getChainAddresses } from "../../lib/chain-addresses";
import { formatNativeBalance } from "../../lib/format";
import { isHexAddress } from "../../lib/hex-utils";
import { getActiveChain, getNativeCurrencySymbol } from "../../lib/wagmi-config";
import { TransactionStateIndicator } from "../shared/TransactionStateIndicator";
import { BalanceDisplay } from "./BalanceDisplay";
import { DefiDisclaimer, isDisclaimerAcknowledged } from "./DefiDisclaimer";
import { PermitApproval } from "./PermitApproval";
import { SwapForm } from "./SwapForm";

type TradePanelProps = {
	tokenId: string;
	tokenAddress: string;
	poolAddress: string;
	tokenSymbol: string;
	tokenDecimals?: number;
	tickSpacing?: number;
};

type SwapButtonState =
	| "enter-amount"
	| "insufficient"
	| "no-liquidity"
	| "approve"
	| "ready"
	| "executing";

function getSwapButtonLabel(state: SwapButtonState): string {
	switch (state) {
		case "enter-amount":
			return "Enter Amount";
		case "insufficient":
			return "Insufficient Balance";
		case "no-liquidity":
			return "No Liquidity";
		case "approve":
			return "Approve";
		case "ready":
			return "Swap";
		case "executing":
			return "Swapping...";
	}
}

export function TradePanel({
	tokenId,
	tokenAddress,
	poolAddress,
	tokenSymbol,
	tokenDecimals = 18,
	tickSpacing = 200,
}: TradePanelProps) {
	const { isConnected, address, chainId, login } = useWallet();

	// Form state (lifted from SwapForm)
	const [direction, setDirection] = useState<"buy" | "sell">("buy");
	const [amountIn, setAmountIn] = useState("");
	const [slippageBps, setSlippageBps] = useState(50);
	const [showDisclaimer, setShowDisclaimer] = useState(false);

	// Quote
	const {
		quote,
		isLoading: isQuoteLoading,
		error: quoteError,
		noLiquidity,
		refetch: refetchQuote,
	} = useSwapQuote({
		tokenAddress,
		poolAddress,
		amountIn,
		direction,
		slippageBps,
		tokenDecimals,
	});

	// Swap execution
	const { execute, txState, reset: resetTx } = useSwap();

	// Balance
	const { balance: tokenBalance, nativeBalance } = useTokenBalance(tokenAddress, tokenDecimals);

	// Chain addresses for swap params — re-evaluates when wallet chain changes.
	// chainId is not used inside the callback but intentionally triggers re-evaluation
	// when the user switches chains, since getActiveChain() reads from global config.
	// biome-ignore lint/correctness/useExhaustiveDependencies: chainId triggers re-evaluation on chain switch
	const chainConfig = useMemo(() => {
		const activeChain = getActiveChain();
		return {
			chainAddresses: getChainAddresses(activeChain.id),
			nativeSymbol: getNativeCurrencySymbol(),
		};
	}, [chainId]);
	const { chainAddresses, nativeSymbol } = chainConfig;

	// Parse amountIn to bigint
	const inputDecimals = direction === "buy" ? 18 : tokenDecimals;
	const amountInWei = useMemo(() => {
		try {
			if (!amountIn || amountIn === "0") return BigInt(0);
			return parseUnits(amountIn, inputDecimals);
		} catch {
			return BigInt(0);
		}
	}, [amountIn, inputDecimals]);

	// Approval state (only for sell direction — buy uses native ETH)
	const {
		needsErc20Approval,
		needsPermit2Approval,
		refetch: refetchAllowance,
	} = usePermit2Allowance(
		tokenAddress,
		address ?? "",
		direction === "sell" ? amountInWei : BigInt(0)
	);
	const needsApproval = direction === "sell" && (needsErc20Approval || needsPermit2Approval);

	// Chain unavailable — only when pool state genuinely failed AND no amount is entered.
	// When the user has entered an amount, show the error inline in SwapForm instead.
	const isChainUnavailable = quoteError !== null && (!amountIn || amountIn === "0");

	// Insufficient balance check
	const isInsufficientBalance = useMemo(() => {
		if (amountInWei === BigInt(0)) return false;
		if (direction === "buy") {
			return nativeBalance !== undefined && amountInWei > nativeBalance;
		}
		return tokenBalance !== undefined && amountInWei > tokenBalance;
	}, [amountInWei, direction, nativeBalance, tokenBalance]);

	// Button state machine
	// noLiquidity only blocks when the quoter also returned no valid quote —
	// the V4 Quoter handles tick-boundary crossing where getLiquidity()=0.
	const buttonState: SwapButtonState = useMemo(() => {
		if (txState.status !== "idle") return "executing";
		if (!amountIn || amountIn === "0") return "enter-amount";
		if (noLiquidity && !quote) return "no-liquidity";
		if (isInsufficientBalance) return "insufficient";
		if (needsApproval) return "approve";
		return "ready";
	}, [txState.status, amountIn, noLiquidity, quote, isInsufficientBalance, needsApproval]);

	// Swap execution handler
	const doExecuteSwap = useCallback(async () => {
		if (!quote || !chainAddresses) return;
		if (!isHexAddress(tokenAddress) || !isHexAddress(poolAddress)) return;
		const weth = chainAddresses.weth;
		const tokenIn = direction === "buy" ? weth : tokenAddress;
		const tokenOut = direction === "buy" ? tokenAddress : weth;
		const tokenIsToken0 = tokenAddress.toLowerCase() < weth.toLowerCase();
		const zeroForOne = direction === "buy" ? !tokenIsToken0 : tokenIsToken0;

		await execute({
			tokenIn,
			tokenOut,
			amountIn: amountInWei,
			amountOutMinimum: quote.minimumReceived,
			poolAddress,
			tickSpacing,
			zeroForOne,
			deadline: BigInt(Math.floor(Date.now() / 1000) + 1800),
			isNativeEthInput: direction === "buy",
			tokenId,
			direction,
		});
	}, [
		quote,
		chainAddresses,
		direction,
		tokenAddress,
		amountInWei,
		poolAddress,
		tickSpacing,
		execute,
		tokenId,
	]);

	const handleSwapClick = useCallback(() => {
		if (!isDisclaimerAcknowledged()) {
			setShowDisclaimer(true);
			return;
		}
		doExecuteSwap();
	}, [doExecuteSwap]);

	const handleDisclaimerAcknowledge = useCallback(() => {
		setShowDisclaimer(false);
		doExecuteSwap();
	}, [doExecuteSwap]);

	const handleTradeAgain = useCallback(() => {
		resetTx();
		setAmountIn("");
	}, [resetTx]);

	const handleMaxClick = useCallback((maxAmount: string) => {
		setAmountIn(maxAmount);
	}, []);

	// ── Rendering ──────────────────────────────────────────

	const panelHeader = <h3 className="text-lg font-semibold text-pastel-black mb-4">Trade</h3>;

	const footerLinks = (
		<div className="flex items-center justify-center gap-4 pt-3 border-t border-pastel-border text-xs text-pastel-gray">
			<a href="/defi-risks" className="hover:text-pastel-black transition-colors">
				DeFi Risks
			</a>
			<span aria-hidden="true">&middot;</span>
			<a href="/terms" className="hover:text-pastel-black transition-colors">
				Terms
			</a>
		</div>
	);

	// Disconnected state
	if (!isConnected) {
		return (
			<div
				className="rounded-2xl border border-pastel-border bg-white p-6"
				data-testid="trade-panel"
			>
				{panelHeader}
				<div className="text-center py-8">
					<p className="text-sm text-pastel-gray mb-4">Connect your wallet to start trading</p>
					<button
						type="button"
						onClick={() => login()}
						className="rounded-lg bg-pastel-vibrant-blue px-6 py-2.5 text-sm font-semibold text-white hover:bg-pastel-vibrant-blue/90 transition-colors"
						data-testid="connect-wallet-button"
					>
						Connect Wallet to Trade
					</button>
				</div>
				{footerLinks}
			</div>
		);
	}

	// Chain unavailable state
	if (isChainUnavailable) {
		return (
			<div
				className="rounded-2xl border border-pastel-border bg-white p-6"
				data-testid="trade-panel"
			>
				{panelHeader}
				<div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center space-y-2">
					<p className="text-sm font-medium text-amber-800">
						Trading unavailable — chain is currently unreachable
					</p>
					<button
						type="button"
						onClick={refetchQuote}
						className="rounded-lg bg-amber-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
						data-testid="chain-retry-button"
					>
						Retry
					</button>
				</div>
				{footerLinks}
			</div>
		);
	}

	// Transaction in progress — show indicator
	if (txState.status !== "idle") {
		return (
			<div
				className="rounded-2xl border border-pastel-border bg-white p-6"
				data-testid="trade-panel"
			>
				{panelHeader}
				<TransactionStateIndicator
					status={txState.status}
					txHash={txState.txHash}
					error={txState.error}
					onRetry={resetTx}
					labels={{
						confirmedTitle: "Swap Complete",
						confirmedDescription: "Your swap has been confirmed on-chain.",
					}}
				/>
				{txState.status === "confirmed" && (
					<button
						type="button"
						onClick={handleTradeAgain}
						className="mt-4 w-full rounded-lg border border-pastel-border px-4 py-2.5 text-sm font-semibold text-pastel-black hover:bg-pastel-bg transition-colors"
						data-testid="trade-again-button"
					>
						Trade Again
					</button>
				)}
				{footerLinks}
			</div>
		);
	}

	// Connected + idle — show full trading form
	return (
		<div
			className="rounded-2xl border border-pastel-border bg-white p-6 space-y-4"
			data-testid="trade-panel"
		>
			{panelHeader}

			<SwapForm
				tokenSymbol={tokenSymbol}
				tokenDecimals={tokenDecimals}
				direction={direction}
				amountIn={amountIn}
				slippageBps={slippageBps}
				onDirectionChange={setDirection}
				onAmountInChange={setAmountIn}
				onSlippageBpsChange={setSlippageBps}
				quote={quote}
				isQuoteLoading={isQuoteLoading}
				quoteError={quoteError}
				noLiquidity={noLiquidity}
				onRetryQuote={refetchQuote}
			/>

			{/* Balance display — sell shows token balance, buy shows native balance */}
			{direction === "sell" ? (
				<BalanceDisplay
					tokenAddress={tokenAddress}
					tokenSymbol={tokenSymbol}
					tokenDecimals={tokenDecimals}
					amountIn={amountIn}
					onMaxClick={handleMaxClick}
				/>
			) : (
				nativeBalance !== undefined && (
					<div className="flex items-center justify-between text-xs text-pastel-gray">
						<span>
							Balance: {formatNativeBalance(nativeBalance)} {nativeSymbol}
						</span>
					</div>
				)
			)}

			{/* Permit2 approval steps (sell direction only) */}
			{direction === "sell" &&
				needsApproval &&
				amountInWei > BigInt(0) &&
				address &&
				isHexAddress(tokenAddress) && (
					<PermitApproval
						tokenAddress={tokenAddress}
						walletAddress={address}
						amountIn={amountInWei}
						onApprovalComplete={refetchAllowance}
					/>
				)}

			{/* Swap button */}
			<button
				type="button"
				onClick={handleSwapClick}
				disabled={buttonState !== "ready"}
				className="w-full rounded-lg bg-pastel-vibrant-blue px-4 py-3 text-sm font-semibold text-white hover:bg-pastel-vibrant-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				data-testid="swap-button"
			>
				{getSwapButtonLabel(buttonState)}
			</button>

			{footerLinks}

			{/* DeFi Disclaimer Dialog */}
			<DefiDisclaimer
				open={showDisclaimer}
				onAcknowledge={handleDisclaimerAcknowledge}
				onCancel={() => setShowDisclaimer(false)}
			/>
		</div>
	);
}
